import React, { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import posthog from "posthog-js";
import { Icon, Button, Modal, Form, Input, Message, Loader, Dropdown, TextArea } from "semantic-ui-react";
import { axiosBasic, getUserInfo } from "../../../../../api/axios";
import { config } from "../../../../../api/endPoints";
import { DEFAULT_PRICE_PER_USER_PER_MONTH } from "../../../../Global/Constant";
import {
  fetchSubscriptionData,
  invalidateSubscriptionCache,
  dispatchSubscriptionChanged,
  getSubscriptionStatusMessage,
  isSubscriptionSuspended,
  normalizeSubscriptionNumber,
} from "../../../../../utils/subscriptionUtils";

import "./Billing.css";
import "../Settings.css";

const Billing = () => {
  const { t } = useTranslation();
  const postRenewRefreshTimerRef = useRef(null);
  const [loading, setLoading] = useState(true);
  const [subscriptionData, setSubscriptionData] = useState(null);
  const [currentMonthBilling, setCurrentMonthBilling] = useState(null);
  const [paymentHistory, setPaymentHistory] = useState([]);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);

  // Modal states
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [showCalculatorModal, setShowCalculatorModal] = useState(false);
  const [showUpdatePriceModal, setShowUpdatePriceModal] = useState(false);
  const [showCheckoutModal, setShowCheckoutModal] = useState(false);
  const [checkoutUrl, setCheckoutUrl] = useState(null);
  const [showPaymentMethodUpdateModal, setShowPaymentMethodUpdateModal] = useState(false);
  const [paymentMethodUpdateEmail, setPaymentMethodUpdateEmail] = useState(null);

  // Calculator states
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [calculatedBilling, setCalculatedBilling] = useState(null);
  const [calculating, setCalculating] = useState(false);

  // Update price states
  const [updatingPrice, setUpdatingPrice] = useState(false);

  // Payment method update link states
  const [sendingPaymentLink, setSendingPaymentLink] = useState(false);

  // Cancel subscription states
  const [showCancelSubscriptionModal, setShowCancelSubscriptionModal] = useState(false);
  const [cancellationReasonId, setCancellationReasonId] = useState(null);
  const [reasonText, setReasonText] = useState('');
  const [cancellingSubscription, setCancellingSubscription] = useState(false);

  // Renew subscription states
  const [showRenewSubscriptionModal, setShowRenewSubscriptionModal] = useState(false);
  const [renewReasonText, setRenewReasonText] = useState('');
  const [renewingSubscription, setRenewingSubscription] = useState(false);

  const { ROOT_URL, REST_API } = config;

  // Cancellation reason options
  const cancellationReasons = [
    { key: 1, text: t("app.billing.cancellationReasonPrice"), value: 1 },
    { key: 2, text: t("app.billing.cancellationReasonNoLongerNeed"), value: 2 },
    { key: 3, text: t("app.billing.cancellationReasonNotSatisfied"), value: 3 },
    { key: 4, text: t("app.billing.cancellationReasonSwitched"), value: 4 },
    { key: 5, text: t("app.billing.cancellationReasonNotPriority"), value: 5 },
    { key: 6, text: t("app.billing.cancellationReasonMistake"), value: 6 },
    { key: 8, text: t("app.billing.cancellationReasonSwitchingPlan"), value: 8 },
    { key: 99, text: t("app.billing.cancellationReasonOther"), value: 99 },
  ];

  // Fetch subscription data (force: bypasses in-flight/cache so Billing always sees fresh API state)
  const fetchSubscription = async () => {
    const userId = getUserInfo()?.selfUserId ?? null;
    const userInput = {};
    try {
      setLoading(true);
      setError(null);
      const data = await fetchSubscriptionData({ force: true });

      const success = !!data;
      if (typeof posthog !== "undefined" && posthog.capture) {
        posthog.capture("fetchSubscription", {
          type: success ? "info" : "exception",
          userId,
          success,
          userInput,
          reason: success ? undefined : "Failed to load subscription",
        });
      }

      if (success) {
        setSubscriptionData(data);
        if (data.currentMonthBilling) {
          setCurrentMonthBilling(data.currentMonthBilling);
        }
      } else {
        setSubscriptionData(null);
        setError(null);
      }
    } catch (err) {
      if (typeof posthog !== "undefined" && posthog.capture) {
        posthog.capture("fetchSubscription", {
          type: "exception",
          userId,
          success: false,
          userInput,
          reason: err?.response?.data?.message ?? err?.message ?? "Request failed",
        });
      }
      setError(err.response?.data?.message || t("app.billing.failedLoadSubscription"));
    } finally {
      setLoading(false);
    }
  };

  // Fetch current month billing
  const fetchCurrentMonthBilling = async () => {
    const userId = getUserInfo()?.selfUserId ?? null;
    const userInput = {};
    try {
      const response = await axiosBasic.get(REST_API.Payment.CurrentMonthBilling);

      const success = response.data && response.data.success;
      if (typeof posthog !== "undefined" && posthog.capture) {
        posthog.capture("fetchCurrentMonthBilling", {
          type: success ? "info" : "exception",
          userId,
          success,
          userInput,
          reason: success ? undefined : (response.data?.message || "Failed to load billing"),
        });
      }
      if (success) {
        setCurrentMonthBilling(response.data.data);
      }
    } catch (err) {
      if (typeof posthog !== "undefined" && posthog.capture) {
        posthog.capture("fetchCurrentMonthBilling", {
          type: "exception",
          userId,
          success: false,
          userInput,
          reason: err?.response?.data?.message ?? err?.message ?? "Request failed",
        });
      }

    }
  };

  // Fetch payment history
  const fetchPaymentHistory = async () => {
    const userId = getUserInfo()?.selfUserId ?? null;
    const userInput = {};
    try {
      const response = await axiosBasic.get(REST_API.Payment.PaymentHistory);

      const success = response.data && response.data.success;
      if (typeof posthog !== "undefined" && posthog.capture) {
        posthog.capture("fetchPaymentHistory", {
          type: success ? "info" : "exception",
          userId,
          success,
          userInput,
          reason: success ? undefined : (response.data?.message || "Failed to load payment history"),
        });
      }
      if (success) {
        setPaymentHistory(response.data.data || []);
      }
    } catch (err) {
      if (typeof posthog !== "undefined" && posthog.capture) {
        posthog.capture("fetchPaymentHistory", {
          type: "exception",
          userId,
          success: false,
          userInput,
          reason: err?.response?.data?.message ?? err?.message ?? "Request failed",
        });
      }

    }
  };

  /** After renew/cancel/checkout: subscription + current month + history + clear shared subscription cache */
  const refreshAllBillingData = async () => {
    invalidateSubscriptionCache();
    await fetchSubscription();
    await Promise.all([fetchCurrentMonthBilling(), fetchPaymentHistory()]);
    dispatchSubscriptionChanged({ source: "billing-refresh" });
  };

  // Calculate billing for custom period
  const calculateBilling = async () => {
    if (!startDate || !endDate) {
      setError(t("app.billing.selectBothDates"));
      return;
    }

    if (new Date(startDate) >= new Date(endDate)) {
      setError(t("app.billing.startBeforeEnd"));
      return;
    }

    setCalculating(true);
    setError(null);
    const userId = getUserInfo()?.selfUserId ?? null;
    const userInput = { hasStartDate: !!startDate, hasEndDate: !!endDate };

    try {
      const response = await axiosBasic.post(REST_API.Payment.CalculateBilling, {
        startDate: new Date(startDate).toISOString(),
        endDate: new Date(endDate).toISOString()
      });

      const success = response.data && response.data.success;
      if (typeof posthog !== "undefined" && posthog.capture) {
        posthog.capture("calculateBilling", {
          type: success ? "info" : "exception",
          userId,
          success,
          userInput,
          reason: success ? undefined : (response.data?.message || "Failed to calculate billing"),
        });
      }
      if (success) {
        setCalculatedBilling(response.data.data);
      } else {
        setError(response.data?.message || t("app.billing.failedCalculateBilling"));
      }
    } catch (err) {
      if (typeof posthog !== "undefined" && posthog.capture) {
        posthog.capture("calculateBilling", {
          type: "exception",
          userId,
          success: false,
          userInput,
          reason: err?.response?.data?.message ?? err?.message ?? "Request failed",
        });
      }

      setError(err.response?.data?.message || t("app.billing.failedCalculateBilling"));
    } finally {
      setCalculating(false);
    }
  };

  // Update subscription price
  const updateSubscriptionPrice = async () => {
    const userId = getUserInfo()?.selfUserId ?? null;
    const userInput = {};
    setUpdatingPrice(true);
    setError(null);
    setSuccessMessage(null);

    try {
      const response = await axiosBasic.post(REST_API.Payment.UpdateSubscriptionPrice, {});

      const success = response.data && response.data.success;
      if (typeof posthog !== "undefined" && posthog.capture) {
        posthog.capture("updateSubscriptionPrice", {
          type: success ? "info" : "exception",
          userId,
          success,
          userInput,
          reason: success ? undefined : (response.data?.message || "Failed to update subscription price"),
        });
      }
      if (success) {
        setSuccessMessage(t("app.billing.priceUpdatedSuccess"));
        await refreshAllBillingData();
        setTimeout(() => {
          setShowUpdatePriceModal(false);
          setSuccessMessage(null);
        }, 2000);
      } else {
        setError(response.data?.message || t("app.billing.failedUpdatePrice"));
      }
    } catch (err) {
      if (typeof posthog !== "undefined" && posthog.capture) {
        posthog.capture("updateSubscriptionPrice", {
          type: "exception",
          userId,
          success: false,
          userInput,
          reason: err?.response?.data?.message ?? err?.message ?? "Request failed",
        });
      }

      setError(err.response?.data?.message || t("app.billing.failedUpdatePrice"));
    } finally {
      setUpdatingPrice(false);
    }
  };

  // Get checkout URL and show confirmation modal
  const handleCheckout = async () => {
    const userId = getUserInfo()?.selfUserId ?? null;
    const userInput = {};
    try {
      setLoading(true);
      setError(null);
      const response = await axiosBasic.get(REST_API.Payment.CheckoutUrl);

      const success = !!(response.data && response.data.success && response.data.data?.checkoutUrl);
      if (typeof posthog !== "undefined" && posthog.capture) {
        posthog.capture("getCheckoutUrl", {
          type: success ? "info" : "exception",
          userId,
          success,
          userInput,
          reason: success ? undefined : (response.data?.message || "Failed to get checkout URL"),
        });
      }
      if (success) {
        // Store checkout URL and show confirmation modal
        setCheckoutUrl(response.data.data.checkoutUrl);
        setShowCheckoutModal(true);
        setLoading(false);
      } else {
        setError(t("app.billing.failedGetCheckoutUrl"));
        setLoading(false);
      }
    } catch (err) {
      if (typeof posthog !== "undefined" && posthog.capture) {
        posthog.capture("getCheckoutUrl", {
          type: "exception",
          userId,
          success: false,
          userInput,
          reason: err?.response?.data?.message ?? err?.message ?? "Request failed",
        });
      }
      setError(err.response?.data?.message || t("app.billing.failedGetCheckoutUrl"));
      setLoading(false);
    }
  };

  // Handle redirect to checkout after user confirms
  const handleConfirmCheckout = () => {
    if (checkoutUrl) {
      try {
        sessionStorage.setItem("hrm_billing_checkout_return", "1");
      } catch (e) {
        /* ignore */
      }
      window.location.href = checkoutUrl;
    }
  };

  // Send payment method update link
  const handleSendPaymentMethodUpdateLink = async () => {
    const userId = getUserInfo()?.selfUserId ?? null;
    const userInput = {};
    try {
      setSendingPaymentLink(true);
      setError(null);
      setSuccessMessage(null);
      const response = await axiosBasic.post(REST_API.Payment.SendPaymentMethodUpdateLink);

      // Check for success - API returns isSuccess (lowercase) and customerEmail (lowercase)
      const isSuccess = response.data?.data?.isSuccess || response.data?.data?.IsSuccess;
      const customerEmail = response.data?.data?.customerEmail || response.data?.data?.CustomerEmail;

      const success = !!(response.data && response.data.success && isSuccess);
      if (typeof posthog !== "undefined" && posthog.capture) {
        posthog.capture("sendPaymentMethodUpdateLink", {
          type: success ? "info" : "exception",
          userId,
          success,
          userInput,
          reason: success ? undefined : (response.data?.message || "Failed to send payment method update link"),
        });
      }
      if (success) {
        setPaymentMethodUpdateEmail(customerEmail || t("app.billing.yourEmail"));
        setShowPaymentMethodUpdateModal(true);
        setSuccessMessage(response.data.message || t("app.billing.linkSentToCustomer"));
      } else {
        const errorMessage = response.data?.message || t("app.billing.failedSendPaymentLink");
        setError(errorMessage);
      }
    } catch (err) {
      if (typeof posthog !== "undefined" && posthog.capture) {
        posthog.capture("sendPaymentMethodUpdateLink", {
          type: "exception",
          userId,
          success: false,
          userInput,
          reason: err?.response?.data?.message ?? err?.message ?? "Request failed",
        });
      }
      const errorMessage = err.response?.data?.message || t("app.billing.failedSendPaymentLink");
      if (err.response?.data?.data?.Errors) {
        const errorDetails = err.response.data.data.Errors
          .map(e => e.PropertyErrorMessages || [])
          .flat()
          .join(', ');
        setError(`${errorMessage}. Details: ${errorDetails}`);
      } else {
        setError(errorMessage);
      }
    } finally {
      setSendingPaymentLink(false);
    }
  };

  // Renew subscription
  const handleRenewSubscription = async () => {
    const userId = getUserInfo()?.selfUserId ?? null;
    const userInput = { hasReasonText: !!(renewReasonText && renewReasonText.trim()) };
    try {
      setRenewingSubscription(true);
      setError(null);
      setSuccessMessage(null);

      // Build request body - match backend property name (ReasonText with capital R)
      const requestBody = {
        ReasonText: renewReasonText && renewReasonText.trim() ? renewReasonText.trim() : null
      };


      const response = await axiosBasic.post(REST_API.Payment.RenewSubscription, requestBody);

      const success = response.data && response.data.success;
      if (typeof posthog !== "undefined" && posthog.capture) {
        posthog.capture("renewSubscription", {
          type: success ? "info" : "exception",
          userId,
          success,
          userInput,
          reason: success ? undefined : (response.data?.message || "Failed to renew subscription"),
        });
      }
      if (success) {
        setSuccessMessage(response.data.message || t("app.billing.renewalSubmitted"));
        setShowRenewSubscriptionModal(false);
        setRenewReasonText('');
        await refreshAllBillingData();
        // Backend / cached API may lag; second pass unlocks UI if first fetch was stale
        if (postRenewRefreshTimerRef.current) {
          window.clearTimeout(postRenewRefreshTimerRef.current);
        }
        postRenewRefreshTimerRef.current = window.setTimeout(() => {
          postRenewRefreshTimerRef.current = null;
          void refreshAllBillingData();
        }, 2500);
      } else {
        setError(response.data?.message || t("app.billing.failedRenewSubscription"));
      }
    } catch (err) {
      if (typeof posthog !== "undefined" && posthog.capture) {
        posthog.capture("renewSubscription", {
          type: "exception",
          userId,
          success: false,
          userInput,
          reason: err?.response?.data?.message ?? err?.message ?? "Request failed",
        });
      }
      if (err.response?.status === 404) {
        setError(t("app.billing.renewEndpointNotFound"));
      } else {
        const errorMessage = err.response?.data?.message || err.message || t("app.billing.failedRenewSubscription");
        setError(errorMessage);
      }
    } finally {
      setRenewingSubscription(false);
    }
  };

  // Cancel subscription
  const handleCancelSubscription = async () => {
    // Validate: cancellationReasonId must be provided
    if (!cancellationReasonId) {
      setError(t("app.billing.selectCancellationReasonRequired"));
      return;
    }

    // Validate: if "Other" is selected, reasonText is required
    if (cancellationReasonId === 99 && !reasonText.trim()) {
      setError(t("app.billing.provideCustomReason"));
      return;
    }

    const userId = getUserInfo()?.selfUserId ?? null;
    const userInput = {
      cancellationReasonId: cancellationReasonId === 99 ? null : cancellationReasonId,
      hasReasonText: !!(reasonText && reasonText.trim()),
      sendCustomerNotification: true
    };
    try {
      setCancellingSubscription(true);
      setError(null);

      // If "Other" is selected, send reasonText with cancellationReasonId as null
      // For "I am not satisfied" (3), send both cancellationReasonId and reasonText (if provided)
      // For other reasons, send cancellationReasonId and reasonText (if provided)
      const requestBody = {
        cancellationReasonId: cancellationReasonId === 99 ? null : cancellationReasonId,
        reasonText: (cancellationReasonId === 99 || (cancellationReasonId === 3 && reasonText.trim())) ? reasonText.trim() : (reasonText.trim() || null),
        sendCustomerNotification: true
      };

      const response = await axiosBasic.post(REST_API.Payment.CancelSubscription, requestBody);

      const success = response.data && response.data.success;
      if (typeof posthog !== "undefined" && posthog.capture) {
        posthog.capture("cancelSubscription", {
          type: success ? "info" : "exception",
          userId,
          success,
          userInput,
          reason: success ? undefined : (response.data?.message || "Failed to cancel subscription"),
        });
      }
      if (success) {
        setSuccessMessage(response.data.message || t("app.billing.cancellationSubmitted"));
        setShowCancelSubscriptionModal(false);
        setCancellationReasonId(null);
        setReasonText('');
        await refreshAllBillingData();
      } else {
        setError(response.data?.message || t("app.billing.failedCancelSubscription"));
      }
    } catch (err) {
      if (typeof posthog !== "undefined" && posthog.capture) {
        posthog.capture("cancelSubscription", {
          type: "exception",
          userId,
          success: false,
          userInput,
          reason: err?.response?.data?.message ?? err?.message ?? "Request failed",
        });
      }
 
      const errorMessage = err.response?.data?.message || t("app.billing.failedCancelSubscription");
      setError(errorMessage);
    } finally {
      setCancellingSubscription(false);
    }
  };

  // Load data on mount; returning from PayPro uses full refresh + subscription-changed for Settings hook
  useEffect(() => {
    let checkoutReturn = false;
    try {
      if (sessionStorage.getItem("hrm_billing_checkout_return") === "1") {
        sessionStorage.removeItem("hrm_billing_checkout_return");
        checkoutReturn = true;
      }
    } catch (e) {
      /* ignore */
    }

    if (checkoutReturn) {
      void refreshAllBillingData();
    } else {
      fetchSubscription();
      fetchCurrentMonthBilling();
      fetchPaymentHistory();
    }
    // Mount-only: refreshAllBillingData is intentionally omitted from deps (new fn each render).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => () => {
    if (postRenewRefreshTimerRef.current) {
      window.clearTimeout(postRenewRefreshTimerRef.current);
      postRenewRefreshTimerRef.current = null;
    }
  }, []);

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 1: // Paid
      case 'paid': return '#43e97b';
      case 2: // Failed
      case 'failed': return '#ff6b6b';
      case 3: // Refunded
      case 'refunded': return '#fa709a';
      case 4: // Partially Refunded
      case 'partially-refunded': return '#fee140';
      default: return '#718096';
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 1: return t("app.billing.paymentStatusPaid");
      case 2: return t("app.billing.paymentStatusFailed");
      case 3: return t("app.billing.paymentStatusRefunded");
      case 4: return t("app.billing.paymentStatusPartiallyRefunded");
      default: return t("app.billing.paymentStatusPending");
    }
  };

  const getPaymentTypeText = (type) => {
    switch (type) {
      case 0: return t("app.billing.paymentTypeTrial");
      case 1: return t("app.billing.paymentTypeSubscription");
      case 2: return t("app.billing.paymentTypeOneTime");
      case 3: return t("app.billing.paymentTypeRefund");
      default: return t("app.billing.paymentTypeUnknown");
    }
  };

  if (loading && !subscriptionData) {
    return (
      <div className="billing-container">
        <Loader active size="large">{t("app.billing.loadingBilling")}</Loader>
      </div>
    );
  }

  const subscription = subscriptionData?.subscription;
  const isInTrial = subscriptionData?.isInTrial || false;
  const trialDaysRemaining = subscriptionData?.trialDaysRemaining || 0;
  const activeUserCount = subscriptionData?.activeUserCount || 0;
  const isCancelledButBillingActive = subscriptionData?.isCancelledButBillingActive ?? false;
  const billingPeriodEnd = subscriptionData?.billingPeriodEnd ?? null;
  const daysRemainingInBillingPeriod = subscriptionData?.daysRemainingInBillingPeriod ?? 0;

  // Subscription is active when billing is active (API's isActive). Use for "start vs update payment" UX.
  const isSubscriptionActive = subscriptionData?.isActive ?? false;

  // Never had a paid subscription: no lastPaymentDate and no PayPro IDs (trial ended, never added card).
  // paymentStatus 0 = Pending (no successful payment) — show Start, not Reactivate, even if backend left PayPro IDs from an abandoned flow.
  const paymentPending = normalizeSubscriptionNumber(subscription?.paymentStatus, -1) === 0;
  const neverHadPaidSubscription =
    paymentPending ||
    (!subscription?.lastPaymentDate &&
      !subscription?.payProOrderId &&
      !subscription?.payProProductId &&
      !subscription?.payProSubscriptionId);

  // When subscription is not active: show "Start" if they never paid / payment still pending, else "Reactivate".
  const showStartSubscription = isInTrial || (!isSubscriptionActive && neverHadPaidSubscription);
  const showReactivateSubscription = !isSubscriptionActive && !neverHadPaidSubscription;

  const subscriptionRestricted = subscriptionData && isSubscriptionSuspended(subscriptionData);
  const suspendedStatusExplanation =
    subscriptionRestricted && getSubscriptionStatusMessage(subscriptionData);

  return (
    <div className="billing-container">
      {/* Header Section */}
      <div className="settings-section">
        <div className="section-header">
          <div className="section-icon">
            <Icon name="dollar sign" className="m-0 mb-1" />
          </div>
          <div className="section-content">
            <h3 className="section-title">{t("app.billing.title")}</h3>
            <p className="section-description">
              {t("app.billing.description")}
            </p>
          </div>
        </div>
      </div>

      {/* Error/Success Messages */}
      {error && (
        <Message negative onDismiss={() => setError(null)}>
          <Message.Header>{t("app.billing.errorHeader")}</Message.Header>
          <p>{error}</p>
        </Message>
      )}
      {successMessage && (
        <Message positive onDismiss={() => setSuccessMessage(null)}>
          <Message.Header>{t("app.billing.successHeader")}</Message.Header>
          <p>{successMessage}</p>
        </Message>
      )}

      {/* Same semantics as SubscriptionRestriction elsewhere: clear notice when subscription is restricted */}
      {subscriptionRestricted && !isCancelledButBillingActive && (
        <div className="settings-page-content" style={{ marginBottom: "20px" }}>
          <Message warning icon>
            <Icon name="warning circle" />
            <Message.Content>
              <Message.Header>{t("app.subscriptionRestriction.subscriptionNotActive")}</Message.Header>
              <p>
                {suspendedStatusExplanation ||
                  t("app.billing.pleaseUpdatePaymentToReactivate")}
              </p>
            </Message.Content>
          </Message>
        </div>
      )}

      {/* Reactivate Subscription Section - Only show when cancelled but billing is active */}
      {isCancelledButBillingActive && (
        <div className="settings-page-content" style={{ marginBottom: "20px" }}>
          <div className="content-card">
            <div style={{
              background: "linear-gradient(135deg, #e8f5e9 0%, #c8e6c9 100%)",
              padding: "30px",
              borderRadius: "12px",
              border: "1px solid #a5d6a7",
              marginBottom: "20px"
            }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: "20px" }}>
                <div style={{ flex: "1", minWidth: "250px" }}>
                  <h3 style={{ margin: "0 0 10px 0", fontSize: "24px", color: "#2e7d32", display: "flex", alignItems: "center", gap: "10px" }}>
                    <Icon name="refresh" style={{ color: "#4CAF50" }} />
                    {t("app.billing.reactivateYourSubscription")}
                  </h3>
                  <p style={{ margin: "0", color: "#388e3c", fontSize: "14px" }}>
                    {t("app.billing.reactivateBannerText", { date: billingPeriodEnd ? formatDate(billingPeriodEnd) : t("app.billing.endOfPeriod") })}
                  </p>
                  {daysRemainingInBillingPeriod > 0 && (
                    <p style={{ margin: "10px 0 0 0", color: "#2e7d32", fontSize: "13px", fontWeight: "600" }}>
                      {t("app.billing.daysRemainingInPeriod", { count: daysRemainingInBillingPeriod, days: daysRemainingInBillingPeriod === 1 ? t("app.billing.day") : t("app.billing.days") })}
                    </p>
                  )}
                </div>
                <div>
                  <Button
                    positive
                    size="large"
                    onClick={() => setShowRenewSubscriptionModal(true)}
                    loading={renewingSubscription}
                    className={`subscribe-button subscribe-button-pulse ${renewingSubscription ? 'loading' : ''}`}
                    style={{
                      background: "#4CAF50",
                      color: "white",
                      padding: "12px 40px",
                      fontSize: "16px",
                      fontWeight: "600",
                      borderRadius: "8px",
                      cursor: renewingSubscription ? 'wait' : 'pointer',
                      boxShadow: "0 4px 12px rgba(76, 175, 80, 0.3)",
                      minWidth: "200px",
                      position: "relative",
                      overflow: "hidden"
                    }}
                  >
                    <Icon name="refresh" />
                    {renewingSubscription ? t("app.billing.reactivating") : t("app.billing.reactivateSubscription")}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Billing Staff Actions - Prominent Section */}
      {!isCancelledButBillingActive && (
        <div className="settings-page-content" style={{ marginBottom: "20px" }}>
          <div className="content-card">
            <div style={{
              background: "linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)",
              padding: "30px",
              borderRadius: "12px",
              border: "1px solid #e0e0e0",
              marginBottom: "20px"
            }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: "20px" }}>
                <div style={{ flex: "1", minWidth: "250px" }}>
                  <h3 style={{ margin: "0 0 10px 0", fontSize: "24px", color: "#333", display: "flex", alignItems: "center", gap: "10px" }}>
                    <Icon name="credit card" style={{ color: "#2196F3" }} />
                    {showStartSubscription
                      ? t("app.billing.startYourSubscription")
                      : showReactivateSubscription
                        ? t("app.billing.reactivateYourSubscription")
                        : t("app.billing.updatePaymentMethod")}
                  </h3>
                  <p style={{ margin: "0", color: "#666", fontSize: "14px" }}>
                    {showStartSubscription
                      ? t("app.billing.startSubscriptionBanner")
                      : showReactivateSubscription
                        ? t("app.billing.reactivateBannerInactive")
                        : t("app.billing.updatePaymentBanner")}
                  </p>
                </div>
                <div>
                  <Button
                    primary
                    size="large"
                    onClick={isSubscriptionActive && !isInTrial ? handleSendPaymentMethodUpdateLink : handleCheckout}
                    loading={isSubscriptionActive && !isInTrial ? sendingPaymentLink : loading}
                    className={`subscribe-button ${(showStartSubscription || showReactivateSubscription) ? 'subscribe-button-pulse' : ''} ${(isSubscriptionActive && !isInTrial ? sendingPaymentLink : loading) ? 'loading' : ''}`}
                    style={{
                      background: "#2196F3",
                      color: "white",
                      padding: "12px 40px",
                      fontSize: "16px",
                      fontWeight: "600",
                      borderRadius: "8px",
                      cursor: (isSubscriptionActive && !isInTrial ? sendingPaymentLink : loading) ? 'wait' : 'pointer',
                      boxShadow: "0 4px 12px rgba(33, 150, 243, 0.3)",
                      minWidth: "200px",
                      position: "relative",
                      overflow: "hidden"
                    }}
                  >
                    <Icon name={isSubscriptionActive && !isInTrial ? "credit card" : "shopping cart"} />
                    {showStartSubscription
                      ? t("app.billing.subscribeNow")
                      : showReactivateSubscription
                        ? t("app.billing.reactivateSubscription")
                        : sendingPaymentLink
                          ? t("app.billing.sendingLink")
                          : t("app.billing.updatePaymentMethod")}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Content Section */}
      <div className="settings-page-content">
        <div className="content-card">
          {/* Info Section */}
          <div className="info-section">
            <div className="info-icon">
              <Icon name="info circle" />
            </div>
            <div className="info-text">
              <h3>{t("app.billing.billingManagement")}</h3>
              <p>{t("app.billing.infoParagraph", { price: DEFAULT_PRICE_PER_USER_PER_MONTH })}</p>
            </div>
          </div>

          {/* Current Month Billing - Prominently Displayed */}
          {currentMonthBilling && (
            <div className="current-month-billing-section">
              <h3 className="section-title">
                <Icon name="dollar sign" />
                {t("app.billing.currentMonthBilling")}
              </h3>
              <div className="billing-card" style={{
                background: "#ffffff",
                border: "1px solid #e0e0e0",
                color: "#333",
                padding: "30px",
                borderRadius: "12px",
                marginBottom: "20px",
                boxShadow: "0 2px 8px rgba(0, 0, 0, 0.08)"
              }}>
                <div className="billing-header" style={{ textAlign: "center", marginBottom: "20px" }}>
                  <h3 style={{ margin: "0 0 10px 0", fontSize: "18px", color: "#666" }}>{t("app.billing.amountDueThisMonth")}</h3>
                  <div style={{ fontSize: "48px", fontWeight: "bold", margin: "10px 0", color: "#2196F3" }}>
                    ${currentMonthBilling.amount?.toFixed(2) || '0.00'} {currentMonthBilling.currency || 'USD'}
                  </div>
                </div>

                <div className="billing-breakdown" style={{
                  background: "#f8f9fa",
                  padding: "20px",
                  borderRadius: "8px",
                  margin: "20px 0"
                }}>
                  <div style={{ display: "flex", justifyContent: "space-between", padding: "10px 0", borderBottom: "1px solid #e0e0e0" }}>
                    <span style={{ color: "#666" }}>{t("app.billing.totalUsers")}</span>
                    <strong style={{ color: "#333" }}>{currentMonthBilling.activeUserCount || 0}</strong>
                  </div>
                  <div style={{ display: "flex", justifyContent: "space-between", padding: "10px 0" }}>
                    <span style={{ color: "#666" }}>{t("app.billing.pricePerUser")}</span>
                    <strong style={{ color: "#333" }}>${currentMonthBilling.pricePerUserPerMonth || DEFAULT_PRICE_PER_USER_PER_MONTH}</strong>
                  </div>
                  <div style={{ display: "flex", justifyContent: "space-between", padding: "10px 0", fontWeight: "bold", fontSize: "16px", marginTop: "10px", paddingTop: "15px", borderTop: "2px solid #e0e0e0" }}>
                    <span style={{ color: "#666" }}>{t("app.billing.calculation")}</span>
                    <span style={{ color: "#2196F3" }}>
                      {currentMonthBilling.activeUserCount || 0} users × ${currentMonthBilling.pricePerUserPerMonth || DEFAULT_PRICE_PER_USER_PER_MONTH} =
                      ${currentMonthBilling.amount?.toFixed(2) || '0.00'}
                    </span>
                  </div>
                </div>

                <div style={{ marginTop: "20px", textAlign: "center", color: "#666" }}>
                  <p>
                    <strong>{t("app.billing.billingPeriod")}</strong> {' '}
                    {formatDate(currentMonthBilling.billingPeriodStart)} -{' '}
                    {formatDate(currentMonthBilling.billingPeriodEnd)}
                  </p>
                  {currentMonthBilling.nextBillingDate && (
                    <p>
                      <strong>{t("app.billing.nextBillingDate")}</strong> {' '}
                      {formatDate(currentMonthBilling.nextBillingDate)}
                    </p>
                  )}
                </div>

                {currentMonthBilling.isInTrial && (
                  <div style={{
                    background: "#e8f5e9",
                    padding: "15px",
                    borderRadius: "6px",
                    marginTop: "20px",
                    textAlign: "center",
                    border: "1px solid #c8e6c9"
                  }}>
                    <p style={{ color: "#2e7d32", margin: 0 }}>ℹ️ {t("app.billing.trialBillingNote")}</p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Subscription Status Section */}
          <div className="current-plan-section">
            <h3 className="section-title">
              <Icon name="star" />
              {t("app.billing.subscriptionStatus")}
            </h3>
            {isInTrial ? (
              <div className="current-plan-card" style={{
                background: "#e8f5e9",
                border: "1px solid #c8e6c9",
                color: "#2e7d32",
                padding: "20px",
                borderRadius: "8px",
                marginBottom: "20px"
              }}>
                <div className="plan-badge">{t("app.billing.trialPeriodActive")}</div>
                <div className="plan-info">
                  <h4>🎉 {t("app.billing.trialPeriodActiveTitle")}</h4>
                  <p className="plan-description">
                    <strong>{t("app.billing.daysRemaining", { count: trialDaysRemaining })}</strong>
                  </p>
                  {subscription?.trialEndDate && (
                    <p className="plan-description">
                      {t("app.billing.yourTrialEndsOn", { date: formatDate(subscription.trialEndDate) })}
                    </p>
                  )}
                  {currentMonthBilling && (
                    <p className="plan-description">
                      {t("app.billing.afterTrialCharged", { price: (currentMonthBilling.pricePerUserPerMonth || DEFAULT_PRICE_PER_USER_PER_MONTH).toFixed(2) })}
                    </p>
                  )}
                </div>
              </div>
            ) : !isSubscriptionActive && !isCancelledButBillingActive ? (
              <div className="current-plan-card" style={{
                background: "#fff3e0",
                border: "1px solid #ffcc80",
                color: "#e65100",
                padding: "20px",
                borderRadius: "8px",
                marginBottom: "20px"
              }}>
                <div className="plan-badge" style={{ background: "#ff9800", color: "white" }}>{t("app.billing.accountInactive")}</div>
                <div className="plan-info">
                  <h4>⚠️ {t("app.billing.accountInactiveTitle")}</h4>
                  <p className="plan-description">
                    {t("app.billing.accountDeactivatedPayment")}
                  </p>
                  {subscription?.deactivationReason && (
                    <p className="plan-description">
                      <strong>{t("app.billing.reason")}</strong> {subscription.deactivationReason}
                    </p>
                  )}
                  {subscription?.lastPaymentFailureDate && (
                    <p className="plan-description">
                      {t("app.billing.lastPaymentFailure", { date: formatDate(subscription.lastPaymentFailureDate) })}
                    </p>
                  )}
                  {subscription?.paymentFailureCount > 0 && (
                    <p className="plan-description">
                      {t("app.billing.paymentFailures", { count: subscription.paymentFailureCount })}
                    </p>
                  )}
                  <p className="plan-description" style={{ marginTop: "15px", fontWeight: "600" }}>
                    {t("app.billing.pleaseUpdatePaymentToReactivate")}
                  </p>
                </div>
              </div>
            ) : isCancelledButBillingActive ? (
              <div className="current-plan-card" style={{
                background: "#fff3e0",
                border: "1px solid #ffcc80",
                color: "#e65100",
                padding: "20px",
                borderRadius: "8px",
                marginBottom: "20px"
              }}>
                <div className="plan-badge" style={{ background: "#ff9800", color: "white" }}>{t("app.billing.subscriptionCancelled")}</div>
                <div className="plan-info">
                  <h4>⚠️ {t("app.billing.subscriptionCancelledTitle")}</h4>
                  <p className="plan-description">
                    {t("app.billing.cancelledButActiveText")}
                  </p>
                  {billingPeriodEnd && (
                    <p className="plan-description">
                      <strong>{t("app.billing.accessEndsOn")}</strong> {formatDate(billingPeriodEnd)}
                    </p>
                  )}
                  {daysRemainingInBillingPeriod > 0 && (
                    <p className="plan-description">
                      <strong>{t("app.billing.daysRemainingLabel", { count: daysRemainingInBillingPeriod, days: daysRemainingInBillingPeriod === 1 ? t("app.billing.day") : t("app.billing.days") })}</strong>
                    </p>
                  )}
                  <p className="plan-description" style={{ marginTop: "15px", fontWeight: "600" }}>
                    {t("app.billing.continueUsingUntil", { date: billingPeriodEnd ? formatDate(billingPeriodEnd) : t("app.billing.endOfBillingPeriod") })}
                  </p>
                </div>
              </div>
            ) : (
              <div className="current-plan-card" style={{
                background: "#e3f2fd",
                border: "1px solid #90caf9",
                color: "#1565c0",
                padding: "20px",
                borderRadius: "8px",
                marginBottom: "20px"
              }}>
                <div className="plan-badge" style={{ background: "#2196F3", color: "white" }}>{t("app.billing.activeSubscription")}</div>
                <div className="plan-info">
                  <h4 style={{ color: "#1565c0" }}>✅ {t("app.billing.activeSubscriptionTitle")}</h4>
                  <p className="plan-description" style={{ color: "#1976d2" }}>
                    {subscription?.nextBillingDate
                      ? t("app.billing.nextBillingDateValue", { date: formatDate(subscription.nextBillingDate) })
                      : t("app.billing.subscriptionIsActive")}
                  </p>
                  <div className="plan-price" style={{ marginTop: "10px" }}>
                    <span className="price-amount" style={{ fontSize: "24px", fontWeight: "bold", color: "#1565c0" }}>${subscription?.pricePerUserPerMonth || DEFAULT_PRICE_PER_USER_PER_MONTH}</span>
                    <span className="price-period" style={{ color: "#1976d2", marginLeft: "8px" }}>{t("app.billing.perUserPerMonth")}</span>
                  </div>
                </div>
              </div>
            )}

            <div className="subscription-details" style={{
              display: "grid",
              gridTemplateColumns: "repeat(2, 1fr)",
              gap: "15px",
              marginTop: "20px"
            }}>
              <div style={{
                display: "flex",
                justifyContent: "space-between",
                padding: "15px",
                background: "#f8f9fa",
                borderRadius: "6px",
                borderLeft: "4px solid #2196F3"
              }}>
                <label style={{ fontWeight: 500, color: "#666" }}>{t("app.billing.status")}</label>
                <span style={{
                  padding: "6px 12px",
                  borderRadius: "4px",
                  fontSize: "12px",
                  fontWeight: "bold",
                  textTransform: "uppercase",
                  background: (isSubscriptionActive || isCancelledButBillingActive) ? "#4CAF50" : "#f44336",
                  color: "white"
                }}>
                  {isCancelledButBillingActive ? t("app.billing.cancelledActiveUntilEnd") : (isSubscriptionActive ? t("app.billing.active") : t("app.billing.inactive"))}
                </span>
              </div>

              <div style={{
                display: "flex",
                justifyContent: "space-between",
                padding: "15px",
                background: "#f8f9fa",
                borderRadius: "6px",
                borderLeft: "4px solid #2196F3"
              }}>
                <label style={{ fontWeight: 500, color: "#666" }}>{t("app.billing.activeUsers")}</label>
                <span><strong>{activeUserCount}</strong></span>
              </div>

              <div style={{
                display: "flex",
                justifyContent: "space-between",
                padding: "15px",
                background: "#f8f9fa",
                borderRadius: "6px",
                borderLeft: "4px solid #2196F3"
              }}>
                <label style={{ fontWeight: 500, color: "#666" }}>{t("app.billing.pricePerUserMonth")}</label>
                <span><strong>${subscription?.pricePerUserPerMonth || DEFAULT_PRICE_PER_USER_PER_MONTH}</strong></span>
              </div>

              <div style={{
                display: "flex",
                justifyContent: "space-between",
                padding: "15px",
                background: "#f8f9fa",
                borderRadius: "6px",
                borderLeft: "4px solid #2196F3"
              }}>
                <label style={{ fontWeight: 500, color: "#666" }}>{t("app.billing.currency")}</label>
                <span><strong>{subscription?.currency || 'USD'}</strong></span>
              </div>
            </div>

          </div>

          {/* Billing History */}
          <div className="billing-history-section">
            <h3 className="section-title">
              <Icon name="history" />
              {t("app.billing.billingHistory")}
            </h3>
            <p className="section-description" style={{ paddingLeft: "1.8%" }}>{t("app.billing.billingHistoryDescription")}</p>

            {paymentHistory.length === 0 ? (
              <div style={{ textAlign: "center", padding: "40px", color: "#999" }}>
                <p>{t("app.billing.noPaymentHistory")}</p>
              </div>
            ) : (
              <div className="billing-history-list">
                {paymentHistory.map((payment) => (
                  <div key={payment.id} className="invoice-card">
                    <div className="invoice-info">
                      <h4 className="invoice-description">
                        {t("app.billing.paymentLabel", { type: getPaymentTypeText(payment.paymentType), date: formatDate(payment.paymentDate || payment.createdDate) })}
                      </h4>
                      <p className="invoice-date">
                        {t("app.billing.period", { start: formatDate(payment.billingPeriodStart), end: formatDate(payment.billingPeriodEnd) })}
                      </p>
                      {payment.payProTransactionId && (
                        <p className="invoice-date" style={{ fontSize: "12px", color: "#666" }}>
                          {t("app.billing.transactionId", { id: payment.payProTransactionId || payment.payProOrderId || t("app.billing.na") })}
                        </p>
                      )}
                    </div>
                    <div className="invoice-amount">
                      <span className="amount">{payment.amount?.toFixed(2) || '0.00'} {payment.currency || 'USD'}</span>
                      <span
                        className="status-badge"
                        style={{ background: getStatusColor(payment.paymentStatus) }}
                      >
                        <Icon name="check circle" style={{ height: 'auto' }} />
                        {getStatusText(payment.paymentStatus)}
                      </span>
                    </div>
                    <div className="invoice-actions">
                      <span style={{ fontSize: "12px", color: "#666" }}>
                        {t("app.billing.usersCount", { count: payment.userCount || 0 })}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Cancel only when billing subscription is active — not isAccountActive (can be true while suspended / unpaid) */}
          {isSubscriptionActive && !isInTrial && !isCancelledButBillingActive && (
            <div style={{
              marginTop: "40px",
              padding: "30px",
              background: "#fff",
              borderRadius: "12px",
              border: "2px solid #ffebee",
              textAlign: "center"
            }}>
              <div style={{ marginBottom: "20px" }}>
                <Icon name="warning sign" size="large" style={{ color: "#f44336", marginBottom: "10px" }} />
                <h3 style={{ margin: "10px 0", color: "#333", fontSize: "18px", fontWeight: "600" }}>
                  {t("app.billing.cancelSubscription")}
                </h3>
                <p style={{ color: "#666", fontSize: "14px", lineHeight: "1.6", margin: "10px 0" }}>
                  {t("app.billing.cancelSubscriptionParagraph")}
                </p>
              </div>
              <Button
                negative
                onClick={() => setShowCancelSubscriptionModal(true)}
                style={{
                  background: "#f44336",
                  color: "white",
                  padding: "12px 32px",
                  fontSize: "15px",
                  fontWeight: "600",
                  borderRadius: "8px",
                  border: "none",
                  cursor: "pointer"
                }}
              >
                <Icon name="cancel" />
                {t("app.billing.cancelSubscription")}
              </Button>
            </div>
          )}
        </div>
      </div>

      {/* Checkout Confirmation Modal */}
      <Modal
        open={showCheckoutModal}
        onClose={() => {
          setShowCheckoutModal(false);
          setCheckoutUrl(null);
        }}
        size="small"
        className="modern-modal checkout-modal"
        dimmer={true}
        closeOnDimmerClick={true}
        closeIcon
      >
        <Modal.Header
          className="checkout-modal-header"
          style={{
            background: "linear-gradient(135deg, #2196F3 0%, #1976D2 100%)",
            color: "white",
            borderRadius: "12px 12px 0 0",
            padding: "20px 24px",
            display: "flex",
            alignItems: "center",
            gap: "12px",
            margin: "-1px -1px 0 -1px",
            marginTop: "0",
            border: "none"
          }}
        >
          <Icon name="lock" style={{ color: "white", margin: 0, fontSize: "20px" }} />
          {t("app.billing.secureCheckoutRedirect")}
        </Modal.Header>
        <Modal.Content style={{ padding: "30px 24px" }}>
          <div style={{ textAlign: "center", padding: "10px 0" }}>
            <div style={{
              width: "80px",
              height: "80px",
              margin: "0 auto 20px",
              background: "linear-gradient(135deg, #e3f2fd 0%, #bbdefb 100%)",
              borderRadius: "50%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center"
            }}>
              <Icon name="lock" size="huge" style={{ color: "#2196F3", margin: 0 }} />
            </div>
            <h3 style={{ marginBottom: "15px", color: "#333", fontSize: "20px", fontWeight: "600" }}>
              {t("app.billing.redirectToCheckout")}
            </h3>
            <p style={{ color: "#666", lineHeight: "1.6", marginBottom: "20px", fontSize: "15px" }}>
              {t("app.billing.redirectPayPro")}
            </p>
            <div style={{
              background: "#f8f9fa",
              padding: "20px",
              borderRadius: "8px",
              marginTop: "20px",
              textAlign: "left",
              border: "1px solid #e0e0e0"
            }}>
              <p style={{ margin: "8px 0", color: "#333", fontSize: "14px", display: "flex", alignItems: "center" }}>
                <Icon name="check circle" style={{ color: "#4CAF50", marginRight: "10px", fontSize: "16px" }} />
                {t("app.billing.paymentEncrypted")}
              </p>
              <p style={{ margin: "8px 0", color: "#333", fontSize: "14px", display: "flex", alignItems: "center" }}>
                <Icon name="check circle" style={{ color: "#4CAF50", marginRight: "10px", fontSize: "16px" }} />
                {t("app.billing.weNeverStorePayment")}
              </p>
              <p style={{ margin: "8px 0", color: "#333", fontSize: "14px", display: "flex", alignItems: "center" }}>
                <Icon name="check circle" style={{ color: "#4CAF50", marginRight: "10px", fontSize: "16px" }} />
                {t("app.billing.sslEncrypted")}
              </p>
            </div>
          </div>
        </Modal.Content>
        <Modal.Actions style={{
          background: "#f8f9fa",
          borderRadius: "0 0 12px 12px",
          padding: "20px 24px",
          borderTop: "1px solid #e0e0e0",
          display: "flex",
          justifyContent: "flex-end",
          gap: "12px"
        }}>
          <Button
            onClick={() => {
              setShowCheckoutModal(false);
              setCheckoutUrl(null);
            }}
            style={{
              cursor: 'pointer',
              padding: "10px 24px",
              fontSize: "14px",
              fontWeight: "500",
              borderRadius: "6px",
              background: "#ffffff",
              color: "#666",
              border: "1px solid #e0e0e0",
              transition: "all 0.2s ease"
            }}
            onMouseEnter={(e) => {
              e.target.style.background = "#f5f5f5";
              e.target.style.borderColor = "#d0d0d0";
            }}
            onMouseLeave={(e) => {
              e.target.style.background = "#ffffff";
              e.target.style.borderColor = "#e0e0e0";
            }}
          >
            {t("app.common.cancel")}
          </Button>
          <Button
            primary
            onClick={handleConfirmCheckout}
            style={{
              background: "linear-gradient(135deg, #2196F3 0%, #1976D2 100%)",
              color: "white",
              border: "none",
              cursor: 'pointer',
              padding: "10px 28px",
              fontSize: "14px",
              fontWeight: "600",
              borderRadius: "6px",
              boxShadow: "0 2px 8px rgba(33, 150, 243, 0.3)",
              transition: "all 0.2s ease",
              display: "flex",
              alignItems: "center",
              gap: "8px"
            }}
            onMouseEnter={(e) => {
              e.target.style.transform = "translateY(-1px)";
              e.target.style.boxShadow = "0 4px 12px rgba(33, 150, 243, 0.4)";
            }}
            onMouseLeave={(e) => {
              e.target.style.transform = "translateY(0)";
              e.target.style.boxShadow = "0 2px 8px rgba(33, 150, 243, 0.3)";
            }}
          >
            <span>{t("app.billing.continueToCheckout")}</span>
            <Icon name="arrow right" />
          </Button>
        </Modal.Actions>
      </Modal>

      {/* Billing Calculator Modal */}
      <Modal
        open={showCalculatorModal}
        onClose={() => {
          setShowCalculatorModal(false);
          setCalculatedBilling(null);
          setStartDate('');
          setEndDate('');
          setError(null);
        }}
        size="small"
        className="modern-modal"
        dimmer={true}
        closeOnDimmerClick={true}
        closeIcon
      >
        <Modal.Header style={{
          background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
          color: "white",
          borderRadius: "12px 12px 0 0"
        }}>
          <Icon name="calculator" />
          {t("app.billing.billingCalculator")}
        </Modal.Header>
        <Modal.Content>
          <Form>
            <Form.Field>
              <label>{t("app.billing.startDate")}</label>
              <Input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
              />
            </Form.Field>
            <Form.Field>
              <label>{t("app.billing.endDate")}</label>
              <Input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
              />
            </Form.Field>
            <Button
              primary
              onClick={calculateBilling}
              loading={calculating}
              disabled={!startDate || !endDate}
              style={{
                background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                border: "none",
                marginTop: "10px",
                cursor: (!startDate || !endDate) ? 'not-allowed' : 'pointer'
              }}
            >
              {t("app.billing.calculateBilling")}
            </Button>
          </Form>

          {calculatedBilling && (
            <div style={{ marginTop: "20px", padding: "20px", background: "#f8f9fa", borderRadius: "8px" }}>
              <h4>{t("app.billing.billingDetails")}</h4>
              <div style={{ marginTop: "15px" }}>
                <div style={{ display: "flex", justifyContent: "space-between", padding: "10px 0" }}>
                  <span><strong>{t("app.billing.totalAmount")}</strong></span>
                  <span style={{ color: "#2196F3", fontWeight: "bold" }}>
                    ${calculatedBilling.amount?.toFixed(2) || '0.00'} {calculatedBilling.currency || 'USD'}
                  </span>
                </div>
                <div style={{ display: "flex", justifyContent: "space-between", padding: "10px 0" }}>
                  <span>{t("app.billing.activeUsers")}</span>
                  <span><strong>{calculatedBilling.activeUserCount || 0}</strong></span>
                </div>
                <div style={{ display: "flex", justifyContent: "space-between", padding: "10px 0" }}>
                  <span>{t("app.billing.daysInPeriod")}</span>
                  <span><strong>{calculatedBilling.days || 0}</strong></span>
                </div>
                <div style={{ display: "flex", justifyContent: "space-between", padding: "10px 0" }}>
                  <span>{t("app.billing.pricePerUserMonth")}</span>
                  <span><strong>${calculatedBilling.pricePerUserPerMonth || DEFAULT_PRICE_PER_USER_PER_MONTH}</strong></span>
                </div>
              </div>
            </div>
          )}
        </Modal.Content>
        <Modal.Actions style={{
          background: "linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)",
          borderRadius: "0 0 12px 12px",
          padding: "16px 24px"
        }}>
          <Button
            onClick={() => {
              setShowCalculatorModal(false);
              setCalculatedBilling(null);
              setStartDate('');
              setEndDate('');
              setError(null);
            }}
            style={{ cursor: 'pointer' }}
          >
            {t("app.common.close")}
          </Button>
        </Modal.Actions>
      </Modal>

      {/* Update Subscription Price Modal */}
      <Modal
        open={showUpdatePriceModal}
        onClose={() => {
          setShowUpdatePriceModal(false);
          setError(null);
          setSuccessMessage(null);
        }}
        size="small"
        className="modern-modal"
        dimmer={true}
        closeOnDimmerClick={true}
        closeIcon
      >
        <Modal.Header style={{
          background: "#e8f5e9",
          color: "#2e7d32",
          borderRadius: "12px 12px 0 0",
          borderBottom: "1px solid #c8e6c9"
        }}>
          <Icon name="refresh" />
          {t("app.billing.updateSubscriptionPrice")}
        </Modal.Header>
        <Modal.Content>
          <p>{t("app.billing.updatePriceParagraph")}</p>
          <p style={{ marginTop: "10px", fontWeight: "bold" }}>
            {t("app.billing.currentActiveUsers", { count: activeUserCount })}
          </p>
          <p style={{ marginTop: "10px" }}>
            {t("app.billing.newMonthlyPrice", { price: `$${(activeUserCount * DEFAULT_PRICE_PER_USER_PER_MONTH).toFixed(2)}` })}
          </p>
        </Modal.Content>
        <Modal.Actions style={{
          background: "linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)",
          borderRadius: "0 0 12px 12px",
          padding: "16px 24px"
        }}>
          <Button
            onClick={() => {
              setShowUpdatePriceModal(false);
            setError(null);
            setSuccessMessage(null);
            }}
            style={{ cursor: 'pointer' }}
          >
            {t("app.common.cancel")}
          </Button>
          <Button
            primary
            onClick={updateSubscriptionPrice}
            loading={updatingPrice}
            style={{
              background: "#66bb6a",
              border: "none",
              color: "white",
              cursor: updatingPrice ? 'wait' : 'pointer'
            }}
          >
            {t("app.billing.updatePrice")}
          </Button>
        </Modal.Actions>
      </Modal>

      {/* Payment Method Update Link Success Modal */}
      <Modal
        open={showPaymentMethodUpdateModal}
        onClose={() => {
          setShowPaymentMethodUpdateModal(false);
          setPaymentMethodUpdateEmail(null);
        }}
        size="small"
        className="modern-modal payment-method-update-modal"
        dimmer={true}
        closeOnDimmerClick={true}
        closeIcon
      >
        <Modal.Header
          className="payment-method-update-modal-header"
          style={{
            background: "linear-gradient(135deg, #4CAF50 0%, #45a049 100%)",
            color: "white",
            borderRadius: "12px 12px 0 0",
            padding: "20px 24px",
            display: "flex",
            alignItems: "center",
            gap: "12px",
            margin: "-1px -1px 0 -1px",
            marginTop: "0",
            border: "none"
          }}
        >
          <Icon name="check circle" style={{ color: "white", margin: 0, fontSize: "20px" }} />
          {t("app.billing.paymentMethodUpdateLinkSent")}
        </Modal.Header>
        <Modal.Content style={{ padding: "30px 24px" }}>
          <div style={{ textAlign: "center", padding: "10px 0" }}>
            <div style={{
              width: "80px",
              height: "80px",
              margin: "0 auto 20px",
              background: "linear-gradient(135deg, #e8f5e9 0%, #c8e6c9 100%)",
              borderRadius: "50%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center"
            }}>
              <Icon name="mail" size="huge" style={{ color: "#4CAF50", margin: 0 }} />
            </div>
            <h3 style={{ marginBottom: "15px", color: "#333", fontSize: "20px", fontWeight: "600" }}>
              {t("app.billing.linkSentSuccessfully")}
            </h3>
            <p style={{ color: "#666", lineHeight: "1.6", marginBottom: "15px", fontSize: "15px" }}>
              {t("app.billing.linkSentToEmail", { email: paymentMethodUpdateEmail })}
            </p>
            <p style={{ color: "#666", lineHeight: "1.6", marginBottom: "20px", fontSize: "14px" }}>
              {t("app.billing.checkEmailToUpdate")}
            </p>
            <div style={{
              background: "#fff3e0",
              padding: "15px",
              borderRadius: "8px",
              marginTop: "20px",
              textAlign: "left",
              border: "1px solid #ffcc80"
            }}>
              <p style={{ margin: 0, color: "#e65100", fontSize: "14px", fontWeight: "600", display: "flex", alignItems: "flex-start", lineHeight: "1.5" }}>
                <Icon name="clock" style={{ color: "#ff9800", marginRight: "8px", fontSize: "16px", marginTop: "2px", flexShrink: 0 }} />
                <span>{t("app.billing.linkValid15Minutes")}</span>
              </p>
            </div>
          </div>
        </Modal.Content>
        <Modal.Actions style={{
          background: "#f8f9fa",
          borderRadius: "0 0 12px 12px",
          padding: "20px 24px",
          borderTop: "1px solid #e0e0e0",
          display: "flex",
          justifyContent: "center"
        }}>
          <Button
            primary
            onClick={() => {
              setShowPaymentMethodUpdateModal(false);
              setPaymentMethodUpdateEmail(null);
            }}
            style={{
              background: "linear-gradient(135deg, #4CAF50 0%, #45a049 100%)",
              color: "white",
              border: "none",
              cursor: 'pointer',
              padding: "10px 28px",
              fontSize: "14px",
              fontWeight: "600",
              borderRadius: "6px"
            }}
          >
            {t("app.billing.gotIt")}
          </Button>
        </Modal.Actions>
      </Modal>

      {/* Cancel Subscription Modal */}
      <Modal
        open={showCancelSubscriptionModal}
        onClose={() => {
          setShowCancelSubscriptionModal(false);
          setCancellationReasonId(null);
          setReasonText('');
          setError(null);
        }}
        size="small"
        className="modern-modal cancel-subscription-modal"
        dimmer={true}
        closeOnDimmerClick={true}
        closeIcon
        style={{
          borderRadius: "20px",
          overflow: "hidden",
          boxShadow: "0 20px 60px rgba(0, 0, 0, 0.15)",
          background: "#fff",
          border: "none"
        }}
      >
        <Modal.Header
          className="cancel-subscription-modal-header"
          style={{
            background: "linear-gradient(135deg, #f44336 0%, #d32f2f 100%)",
            backgroundColor: "transparent",
            color: "white",
            padding: "24px 24px 16px 24px",
            borderBottom: "1px solid rgba(255, 255, 255, 0.2)",
            borderRadius: "20px 20px 0 0",
            margin: 0,
            overflow: "hidden",
            borderTopLeftRadius: "20px",
            borderTopRightRadius: "20px"
          }}
        >
          <div style={{ display: "flex", alignItems: "center" }}>
            <div
              style={{
                width: "40px",
                height: "40px",
                borderRadius: "50%",
                background: "rgba(255, 255, 255, 0.2)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                marginRight: "12px"
              }}
            >
              <Icon name="cancel" size="large" style={{ color: "white", margin: 0 }} />
            </div>
            <div>
              <h4 style={{ color: "white", fontSize: "20px", margin: 0, marginBottom: "4px", fontWeight: "600" }}>{t("app.billing.cancelSubscriptionModalTitle")}</h4>
              <p style={{ color: "rgba(255, 255, 255, 0.9)", fontSize: "13px", margin: 0 }}>
                {t("app.billing.sorryToSeeYouGo")}
              </p>
            </div>
          </div>
        </Modal.Header>
        <Modal.Content style={{
          padding: "25px",
          background: "#fff",
          borderRadius: 0,
          margin: 0
        }}>
          <div style={{ marginBottom: "20px" }}>
            <p style={{ fontSize: "15px", lineHeight: "1.6", color: "#333", marginBottom: "15px" }}>
              {t("app.billing.pleaseTellUsWhy")}
            </p>
            {error && (
              <Message negative style={{ marginBottom: "15px" }}>
                {error}
              </Message>
            )}
            {successMessage && (
              <Message positive style={{ marginBottom: "15px" }}>
                {successMessage}
              </Message>
            )}
          </div>

          <Form>
            <Form.Field required>
              <label style={{ fontWeight: "600", marginBottom: "8px" }}>
                {t("app.billing.cancellationReason")}
              </label>
              <Dropdown
                placeholder={t("app.billing.selectReasonOptional")}
                fluid
                selection
                options={cancellationReasons}
                value={cancellationReasonId}
                onChange={(e, { value }) => {
                  setCancellationReasonId(value);
                  // Don't clear custom reason - allow user to add additional details
                }}
                style={{ marginBottom: "15px" }}
              />
              <p style={{ fontSize: "12px", color: "#666", marginTop: "5px", marginBottom: "0" }}>
                {t("app.billing.selectCancellationReason")}
              </p>
            </Form.Field>

            {/* Custom Reason Field - Only show for "Other" or "I am not satisfied with the product" */}
            {(cancellationReasonId === 99 || cancellationReasonId === 3) && (
              <Form.Field required={cancellationReasonId === 99}>
                <label style={{ fontWeight: "600", marginBottom: "8px" }}>
                  {t("app.billing.customReason")}
                </label>
                <TextArea
                  placeholder={
                    cancellationReasonId === 99
                      ? t("app.billing.provideReasonPlaceholder")
                      : t("app.billing.tellUsMorePlaceholder")
                  }
                  value={reasonText}
                  onChange={(e) => setReasonText(e.target.value)}
                  rows={4}
                  disabled={false}
                  style={{
                    minHeight: "100px",
                    resize: "vertical"
                  }}
                />
                {cancellationReasonId === 99 && (
                  <p style={{ fontSize: "12px", color: "#666", marginTop: "5px", marginBottom: "0" }}>
                    {t("app.billing.requiredProvideReason")}
                  </p>
                )}
                {cancellationReasonId === 3 && (
                  <p style={{ fontSize: "12px", color: "#666", marginTop: "5px", marginBottom: "0" }}>
                    {t("app.billing.optionalWeAppreciate")}
                  </p>
                )}
              </Form.Field>
            )}

            <div style={{
              background: "#fff3e0",
              padding: "15px",
              borderRadius: "8px",
              marginTop: "20px",
              border: "1px solid #ffcc80"
            }}>
              <p style={{ margin: 0, color: "#e65100", fontSize: "13px", lineHeight: "1.5", display: "flex", alignItems: "flex-start" }}>
                <Icon name="info circle" style={{ color: "#ff9800", marginRight: "8px", marginTop: "2px", flexShrink: 0 }} />
                <span>
                  {t("app.billing.noteSubscriptionRemainsActive")}
                </span>
              </p>
            </div>
          </Form>
        </Modal.Content>
        <Modal.Actions style={{
          background: "#f8f9fa",
          borderRadius: "0 0 20px 20px",
          padding: "20px 24px",
          borderTop: "1px solid #e0e0e0",
          display: "flex",
          justifyContent: "space-between",
          margin: 0,
          overflow: "hidden"
        }}>
          <Button
            onClick={() => {
              setShowCancelSubscriptionModal(false);
            setCancellationReasonId(null);
            setReasonText('');
            setError(null);
            }}
            disabled={cancellingSubscription}
            style={{
              background: "#6c757d",
              color: "white",
              border: "none",
              cursor: cancellingSubscription ? 'not-allowed' : 'pointer',
              padding: "10px 28px",
              fontSize: "14px",
              fontWeight: "600",
              borderRadius: "6px"
            }}
          >
            {t("app.common.cancel")}
          </Button>
          <Button
            negative
            onClick={handleCancelSubscription}
            loading={cancellingSubscription}
            disabled={cancellingSubscription || !cancellationReasonId || (cancellationReasonId === 99 && !reasonText.trim())}
            style={{
              background: "#f44336",
              color: "white",
              border: "none",
              cursor: (cancellingSubscription || !cancellationReasonId || (cancellationReasonId === 99 && !reasonText.trim())) ? 'not-allowed' : 'pointer',
              padding: "10px 28px",
              fontSize: "14px",
              fontWeight: "600",
              borderRadius: "6px"
            }}
          >
            <Icon name="cancel" />
            {t("app.billing.confirmCancellation")}
          </Button>
        </Modal.Actions>
      </Modal>

      {/* Renew Subscription Modal */}
      <Modal
        open={showRenewSubscriptionModal}
        onClose={() => {
          setShowRenewSubscriptionModal(false);
          setRenewReasonText('');
          setError(null);
        }}
        size="small"
        className="modern-modal renew-subscription-modal"
        dimmer={true}
        closeOnDimmerClick={true}
        closeIcon
        style={{
          borderRadius: "20px",
          overflow: "hidden",
          boxShadow: "0 20px 60px rgba(0, 0, 0, 0.15)",
          background: "#fff",
          border: "none"
        }}
      >
        <Modal.Header
          className="renew-subscription-modal-header"
          style={{
            background: "linear-gradient(135deg, #4CAF50 0%, #45a049 100%)",
            backgroundColor: "transparent",
            color: "white",
            padding: "24px 24px 16px 24px",
            borderBottom: "1px solid rgba(255, 255, 255, 0.2)",
            borderRadius: "20px 20px 0 0",
            margin: 0,
            overflow: "hidden",
            borderTopLeftRadius: "20px",
            borderTopRightRadius: "20px"
          }}
        >
          <div style={{ display: "flex", alignItems: "center" }}>
            <div
              style={{
                width: "40px",
                height: "40px",
                borderRadius: "50%",
                background: "rgba(255, 255, 255, 0.2)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                marginRight: "12px"
              }}
            >
              <Icon name="refresh" size="large" style={{ color: "white", margin: 0 }} />
            </div>
            <div>
              <h4 style={{ color: "white", fontSize: "20px", margin: 0, marginBottom: "4px", fontWeight: "600" }}>{t("app.billing.reactivateSubscriptionModalTitle")}</h4>
              <p style={{ color: "rgba(255, 255, 255, 0.9)", fontSize: "13px", margin: 0 }}>
                {t("app.billing.continueSubscriptionService")}
              </p>
            </div>
          </div>
        </Modal.Header>
        <Modal.Content style={{
          padding: "25px",
          background: "#fff",
          borderRadius: 0,
          margin: 0
        }}>
          <div style={{ marginBottom: "20px" }}>
            <p style={{ fontSize: "15px", lineHeight: "1.6", color: "#333", marginBottom: "15px" }}>
              {t("app.billing.gladYouStaying")}
            </p>
            {error && (
              <Message negative style={{ marginBottom: "15px" }}>
                {error}
              </Message>
            )}
            {successMessage && (
              <Message positive style={{ marginBottom: "15px" }}>
                {successMessage}
              </Message>
            )}
          </div>

          <Form>
            <Form.Field>
              <label style={{ fontWeight: "600", marginBottom: "8px" }}>
                {t("app.billing.reasonReactivationOptional")}
              </label>
              <TextArea
                placeholder={t("app.billing.reactivationPlaceholder")}
                value={renewReasonText}
                onChange={(e) => setRenewReasonText(e.target.value)}
                rows={4}
                style={{
                  minHeight: "100px",
                  resize: "vertical"
                }}
              />
              <p style={{ fontSize: "12px", color: "#666", marginTop: "5px", marginBottom: "0" }}>
                {t("app.billing.optionalShareReason")}
              </p>
            </Form.Field>

            <div style={{
              background: "#e8f5e9",
              padding: "15px",
              borderRadius: "8px",
              marginTop: "20px",
              border: "1px solid #c8e6c9"
            }}>
              <p style={{ margin: 0, color: "#2e7d32", fontSize: "13px", lineHeight: "1.5", display: "flex", alignItems: "flex-start" }}>
                <Icon name="info circle" style={{ color: "#4CAF50", marginRight: "8px", marginTop: "2px", flexShrink: 0 }} />
                <span>
                  {t("app.billing.noteReactivatedBeyondPeriod")}
                </span>
              </p>
            </div>
          </Form>
        </Modal.Content>
        <Modal.Actions style={{
          background: "#f8f9fa",
          borderRadius: "0 0 20px 20px",
          padding: "20px 24px",
          borderTop: "1px solid #e0e0e0",
          display: "flex",
          justifyContent: "space-between",
          margin: 0,
          overflow: "hidden"
        }}>
          <Button
            onClick={() => {
            setShowRenewSubscriptionModal(false);
            setRenewReasonText('');
            setError(null);
            }}
            disabled={renewingSubscription}
            style={{
              background: "#6c757d",
              color: "white",
              border: "none",
              cursor: renewingSubscription ? 'not-allowed' : 'pointer',
              padding: "10px 28px",
              fontSize: "14px",
              fontWeight: "600",
              borderRadius: "6px"
            }}
          >
            {t("app.common.cancel")}
          </Button>
          <Button
            positive
            onClick={handleRenewSubscription}
            loading={renewingSubscription}
            disabled={renewingSubscription}
            style={{
              background: "#4CAF50",
              color: "white",
              border: "none",
              cursor: renewingSubscription ? 'not-allowed' : 'pointer',
              padding: "10px 28px",
              fontSize: "14px",
              fontWeight: "600",
              borderRadius: "6px"
            }}
          >
            <Icon name="check" />
            {t("app.billing.confirmReactivation")}
          </Button>
        </Modal.Actions>
      </Modal>
    </div>
  );
};

export default Billing;
